import { messaging } from '../firebase';
import { 
  getToken, 
  onMessage, 
  deleteToken,
  isSupported as isMessagingSupported 
} from 'firebase/messaging';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

export interface NotificationPermission {
  granted: boolean;
  denied: boolean;
  default: boolean;
}

export interface PushNotificationData {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: Record<string, string>;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
}

class PushNotificationService {
  private messagingInstance: any = null;
  private isInitialized = false;
  private isInitializing = false;
  private initializationPromise: Promise<boolean> | null = null;
  private isSupportedCache: boolean | null = null;

  async initialize(): Promise<boolean> {
    // If already initialized, return immediately
    if (this.isInitialized) return true;
    
    // If initialization is in progress, wait for it
    if (this.isInitializing && this.initializationPromise) {
      return this.initializationPromise;
    }

    // Start initialization
    this.isInitializing = true;
    this.initializationPromise = this._doInitialize();
    
    try {
      const result = await this.initializationPromise;
      return result;
    } finally {
      this.isInitializing = false;
    }
  }

  private async _doInitialize(): Promise<boolean> {
    try {
      logger.debug('🔧 Initializing Firebase Cloud Messaging...');
      
      // Check if service worker is registered
      if ('serviceWorker' in navigator) {
        logger.debug('🔍 Checking for Firebase messaging service worker...');
        
        // Check all service worker registrations
        const registrations = await navigator.serviceWorker.getRegistrations();
        logger.debug('📋 All service worker registrations:', registrations);
        
        const firebaseRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
        logger.debug('📋 Firebase messaging service worker registration:', firebaseRegistration);
        
        if (!firebaseRegistration) {
          logger.debug('⚠️ Firebase messaging service worker not found, trying to register...');
          try {
            const newRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
            logger.debug('✅ Firebase messaging service worker registered:', newRegistration);
          } catch (swError) {
            logger.warn('⚠️ Could not register Firebase messaging service worker:', swError);
          }
        }
      }
      
      const supported = await isMessagingSupported();
      logger.debug('📱 FCM Supported:', supported);
      
      if (!supported) {
        // Only log warning once (first time we detect it's not supported)
        if (this.isSupportedCache === null) {
          logger.warn('❌ Firebase Cloud Messaging is not supported in this browser');
        }
        // Cache the support status to avoid repeated checks
        this.isSupportedCache = false;
        this.isInitialized = true; // Mark as initialized even if not supported
        return false;
      }
      
      // Cache the support status
      this.isSupportedCache = true;

      logger.debug('🔄 Getting messaging instance...');
      this.messagingInstance = await messaging;
      logger.debug('✅ Messaging instance obtained:', this.messagingInstance);
      
      this.isInitialized = true;
      logger.info('✅ Firebase Cloud Messaging initialized successfully');
      return true;
    } catch (error) {
      logger.error('❌ Failed to initialize Firebase Cloud Messaging:', error);
      this.isInitialized = true; // Mark as initialized to prevent retry loops
      return false;
    }
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.messagingInstance) {
      return { granted: false, denied: false, default: true };
    }

    // Check current permission status first
    if (Notification.permission === 'denied') {
      logger.warn('⚠️ Notification permission has been blocked. User must enable it in browser settings.');
      return { granted: false, denied: true, default: false };
    }

    if (Notification.permission === 'granted') {
      logger.debug('✅ Notification permission already granted');
      return { granted: true, denied: false, default: false };
    }

    try {
      const permission = await Notification.requestPermission();
      const result = {
        granted: permission === 'granted',
        denied: permission === 'denied',
        default: permission === 'default'
      };
      
      if (result.granted) {
        logger.info('✅ Notification permission granted');
      } else if (result.denied) {
        logger.warn('⚠️ Notification permission denied by user');
      } else {
        logger.debug('ℹ️ Notification permission request dismissed');
      }
      
      return result;
    } catch (error) {
      logger.error('Error requesting notification permission:', error);
      return { granted: false, denied: false, default: true };
    }
  }

  async getToken(): Promise<string | null> {
    logger.debug('🔑 Getting FCM token...');
    
    if (!this.isInitialized) {
      logger.debug('🔄 Service not initialized, initializing now...');
      await this.initialize();
    }

    if (!this.messagingInstance) {
      logger.warn('❌ No messaging instance available');
      return null;
    }

    // Check notification permission before requesting token
    if (Notification.permission !== 'granted') {
      logger.warn('⚠️ Notification permission not granted. Requesting permission...');
      const permissionResult = await this.requestPermission();
      if (!permissionResult.granted) {
        logger.warn('❌ Cannot get FCM token without notification permission');
        return null;
      }
    }

    try {
      logger.debug('🔑 Requesting FCM token with VAPID key...');
      const currentToken = await getToken(this.messagingInstance, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY || 'BCej_4UonAxDhwUN-CPrhGOLDqgwnLSuYhYBKUNJ0fjsNEZkuriBqxLekMCbqKdOOh8wWe7bION7uxo16bdspwA'
      });

      if (currentToken) {
        logger.info('✅ FCM token generated successfully:', currentToken.substring(0, 20) + '...');
        return currentToken;
      } else {
        logger.warn('❌ No registration token available');
        return null;
      }
    } catch (error) {
      logger.error('❌ Error occurred while retrieving token:', error);
      return null;
    }
  }

  async saveTokenToUser(userId: string, token: string): Promise<void> {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        await updateDoc(userDocRef, {
          fcmToken: token,
          notificationSettings: {
            orderUpdates: true,
            shippingUpdates: true,
            couponExpiry: true,
            promotionalOffers: false,
            lastUpdated: new Date()
          }
        });
      } else {
        await setDoc(userDocRef, {
          fcmToken: token,
          notificationSettings: {
            orderUpdates: true,
            shippingUpdates: true,
            couponExpiry: true,
            promotionalOffers: false,
            lastUpdated: new Date()
          }
        });
      }
    } catch (error) {
      logger.error('Error saving FCM token to user:', error);
    }
  }

  async deleteToken(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (!this.messagingInstance) {
      return;
    }

    try {
      await deleteToken(this.messagingInstance);
      logger.info('Token deleted successfully');
    } catch (error) {
      logger.error('Error deleting token:', error);
    }
  }

  async removeTokenFromUser(userId: string): Promise<void> {
    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        fcmToken: null,
        notificationSettings: {
          orderUpdates: false,
          shippingUpdates: false,
          couponExpiry: false,
          promotionalOffers: false,
          lastUpdated: new Date()
        }
      });
    } catch (error) {
      logger.error('Error removing FCM token from user:', error);
    }
  }

  onMessageReceived(callback: (payload: any) => void): (() => void) | null {
    if (!this.isInitialized || !this.messagingInstance) {
      return null;
    }

    try {
      return onMessage(this.messagingInstance, callback);
    } catch (error) {
      logger.error('Error setting up message listener:', error);
      return null;
    }
  }

  async updateNotificationSettings(
    userId: string, 
    settings: {
      orderUpdates?: boolean;
      shippingUpdates?: boolean;
      couponExpiry?: boolean;
      promotionalOffers?: boolean;
    }
  ): Promise<void> {
    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        notificationSettings: {
          ...settings,
          lastUpdated: new Date()
        }
      });
    } catch (error) {
      logger.error('Error updating notification settings:', error);
    }
  }

  async getNotificationSettings(userId: string): Promise<any> {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        return userDoc.data().notificationSettings || {
          orderUpdates: true,
          shippingUpdates: true,
          couponExpiry: true,
          promotionalOffers: false
        };
      }
      
      return {
        orderUpdates: true,
        shippingUpdates: true,
        couponExpiry: true,
        promotionalOffers: false
      };
    } catch (error) {
      logger.error('Error getting notification settings:', error);
      return {
        orderUpdates: true,
        shippingUpdates: true,
        couponExpiry: true,
        promotionalOffers: false
      };
    }
  }

  // Helper method to show a local notification
  showLocalNotification(data: PushNotificationData): void {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    const notification = new Notification(data.title, {
      body: data.body,
      icon: data.icon || '/favicon.ico',
      badge: data.badge,
      tag: data.tag,
      data: data.data
    });

    // Auto-close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);

    // Handle notification click
    notification.onclick = () => {
      notification.close();
      window.focus();
      
      // Navigate to relevant page based on notification type
      if (data.data?.type === 'order_update') {
        window.location.href = '/dashboard/orders';
      } else if (data.data?.type === 'coupon_expiry') {
        window.location.href = '/dashboard';
      }
    };
  }
}

export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
